import React from "react";

import { colors } from "@zapalacorp/tokens";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";

import ArrowDown from "../../../../../../../components/Generic/Icons/ArrowDown";
import Check from "../../../../../../../components/Generic/Icons/Check";
import InProgressIcon from "../../../../../../../components/Generic/Icons/InProgressIcon";
import { DefaultTooltip } from "../../../../../../../components/Generic/Tooltip";
import { CustomerPortalMilestoneStep } from "../../../../../../../models/customer.portal";
import { capitalizeFirstLetter } from "../../../../../../../util/capitalizeFirstLetter";
import {
  CustomerPortalMilestoneStepStatus,
  MilestonesDefaultTasksTitles,
} from "../../../../../../../util/enums";
import { BadgeStatusByAction } from "../ActionStatusBadge";
import { SubStepAction } from "../SubStepAction";
import {
  BadgePending,
  BadgeProgress,
  BadgeSuccess,
  Box,
  DateRangeText,
  LinePending,
  LineSuccess,
  StepActionTitle,
  StyledVeticalWrapper,
  TitlePending,
  TitleProgress,
  TitleSuccess,
  TitleWrapper,
  Value,
  VeticalWrapper,
} from "./styles";

const formatDateAndTimeValue = (date: Date, includeHours: boolean) => {
  if (includeHours) {
    const formattedDate = format(date, "dd/MM/yyyy • hh:mm a");

    return formattedDate;
  }

  const formattedDate = format(date, "dd/MM/yyyy");

  return formattedDate;
};

interface IStepAction {
  stepStatus: CustomerPortalMilestoneStepStatus;
  title: string;
  subSteps: CustomerPortalMilestoneStep[];
  closeMilestoneDrawer: () => void;
  onOpenShipment: (shipmentId: number) => void;
  actionDate?: string | null;
  hideLine?: boolean;
}

export const StepAction = ({
  stepStatus,
  title,
  subSteps,
  closeMilestoneDrawer,
  onOpenShipment,
  actionDate,
  hideLine,
}: IStepAction) => {
  const { t: translate } = useTranslation();

  const getMinMaxDates = (steps: CustomerPortalMilestoneStep[]): string | null => {
    if (!steps || steps.length === 0) {
      return translate("noStepsInformed");
    }

    const stepsDates = steps.filter((step) => step.actionDate);

    if (stepsDates.length) {
      const dates = stepsDates.map((date) => new Date(date.actionDate || "").getTime());
      const minDate = Math.min(...dates);
      const maxDate = Math.max(...dates);

      if (minDate === maxDate && minDate === 0) {
        return translate("dateNotInformed");
      }

      const formattedMinDate = formatDateAndTimeValue(new Date(minDate), false);
      const formattedMaxDate = formatDateAndTimeValue(new Date(maxDate), false);

      return `${formattedMinDate} ~ ${formattedMaxDate}`;
    }

    return translate("dateNotInformed");
  };

  const convertTitleToValidTranslateKey = (title: string) => {
    switch (title) {
      case MilestonesDefaultTasksTitles.initialRequest:
        return capitalizeFirstLetter(translate("initialRequest"));
      case MilestonesDefaultTasksTitles.completed:
        return capitalizeFirstLetter(translate("completed"));
      case MilestonesDefaultTasksTitles.survey:
        return capitalizeFirstLetter(translate("survey"));
      case MilestonesDefaultTasksTitles.tasks:
        return capitalizeFirstLetter(translate("tasks"));

      default:
        return undefined;
    }
  };

  let components = {
    badge: (
      <BadgeSuccess>
        <Check width={16} height={16} color={colors.white} />
      </BadgeSuccess>
    ),
    line: <LineSuccess />,
    title: <TitleSuccess>{convertTitleToValidTranslateKey(title)}</TitleSuccess>,
  };

  switch (stepStatus) {
    case CustomerPortalMilestoneStepStatus.PENDING:
      components = {
        badge: (
          <BadgePending>
            <InProgressIcon height={16} width={16} color={colors.gray500} />
          </BadgePending>
        ),
        line: <LinePending />,
        title: <TitlePending>{convertTitleToValidTranslateKey(title)}</TitlePending>,
      };
      break;
    case CustomerPortalMilestoneStepStatus.IN_PROGRESS:
      components = {
        badge: (
          <BadgeProgress>
            <ArrowDown width={16} height={16} color={colors.white} />
          </BadgeProgress>
        ),
        line: <LinePending />,
        title: <TitleProgress>{convertTitleToValidTranslateKey(title)}</TitleProgress>,
      };
      break;
    case CustomerPortalMilestoneStepStatus.COMPLETED:
      components = {
        badge: (
          <BadgeSuccess>
            <Check width={16} height={16} color={colors.white} />
          </BadgeSuccess>
        ),
        line: <LineSuccess />,
        title: <TitleSuccess>{convertTitleToValidTranslateKey(title)}</TitleSuccess>,
      };
      break;
    default:
  }

  return (
    <Box>
      <VeticalWrapper>
        {components.badge}
        {!hideLine && components.line}
      </VeticalWrapper>
      <StyledVeticalWrapper gap="4px" padding="0px 12px">
        <TitleWrapper>
          <StepActionTitle>{components.title}</StepActionTitle>
          {stepStatus === CustomerPortalMilestoneStepStatus.IN_PROGRESS && subSteps && (
            <DateRangeText>{getMinMaxDates(subSteps)}</DateRangeText>
          )}
          {stepStatus !== CustomerPortalMilestoneStepStatus.IN_PROGRESS && actionDate && (
            <Value>{actionDate && formatDateAndTimeValue(new Date(actionDate), true)}</Value>
          )}
        </TitleWrapper>

        {subSteps && subSteps.length >= 1 && (
          <StyledVeticalWrapper gap="16px" padding="12px 8px 20px">
            {subSteps.map((step) => {
              return (
                <StyledVeticalWrapper gap="4px" key={`sub-step ${step.action}`}>
                  <Box>
                    <DefaultTooltip title={step.action} placement="top">
                      <StepActionTitle>{step.action}</StepActionTitle>
                    </DefaultTooltip>
                    {BadgeStatusByAction({ value: step.status })}
                  </Box>
                  {step.steps && (
                    <SubStepAction
                      stepDetails={step.steps}
                      shipmentId={step.shipmentId}
                      closeMilestoneDrawer={closeMilestoneDrawer}
                      onOpenShipment={onOpenShipment}
                    />
                  )}
                  <Value>
                    {step?.actionDate && formatDateAndTimeValue(new Date(step.actionDate), true)}
                  </Value>
                </StyledVeticalWrapper>
              );
            })}
          </StyledVeticalWrapper>
        )}
      </StyledVeticalWrapper>
    </Box>
  );
};
